import { FC, ReactElement, useId } from 'react';
import { getImageProps } from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import { imageAmplienceToUrl } from 'helpers/utils/amplience/imageAmplienceToUrl';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { getPageType } from 'helpers/utils/cjTagDataHelpers';
import { cn } from 'helpers/utils/classnames';
import { gtmHeroInteraction } from 'helpers/utils/gtm-events/gtmEccomerce';
import { gtmClickCta } from 'helpers/utils/gtm-events/gtmInteraction';
import { layout } from 'static/constants';
import { AmpPromotionEvent } from '../../../../types/gtm-events/Ecommerce';
import GTMWrapper from '../gtm-wrapper';
import { AmplienceImage } from '../types';
import { HeroBannerCopyPlacement } from './enum';

export interface AmplienceHeroBannerProps {
  title: string;
  subtitle?: string;
  cta?: string;
  index?: number;
  ctaLink?: string;
  imageAltText: string;
  image: AmplienceImage;
  mobileImage?: AmplienceImage;
  copyPlacement: keyof typeof HeroBannerCopyPlacement;
  isOnCarouselView?: boolean;
  isFromCarousel?: boolean;
  gtmEvents: AmpPromotionEvent[];
}

const AmplienceHeroBanner: FC<AmplienceHeroBannerProps> = ({
  ctaLink,
  image,
  imageAltText,
  mobileImage,
  index,
  title,
  copyPlacement,
  cta,
  isFromCarousel,
  subtitle,
  isOnCarouselView = true,
  gtmEvents,
}) => {
  const path = usePathname();
  const pageType = getPageType({
    locale: 'en',
    path: path,
  });
  const hasCopyContent = title?.trim() || subtitle?.trim() || (cta?.trim() && ctaLink?.trim());

  const commonImageProps = {
    loader: amplienceImageLoader,
    fill: true,
    alt: imageAltText || '',
    loading: isOnCarouselView ? ('eager' as const) : ('lazy' as const),
    priority: isOnCarouselView,
    fetchPriority: 'high' as const,
  };

  const mobileImageResult = getImageProps({
    ...commonImageProps,
    src: imageAmplienceToUrl(mobileImage || image),
    sizes: '100vw',
  });
  const mobileImageProps = mobileImageResult.props;

  const desktopImageResult = getImageProps({
    ...commonImageProps,
    src: imageAmplienceToUrl(image),
    sizes: `${layout.pageMaxWidth}px`,
  });
  const desktopSrcSet = desktopImageResult.props.srcSet;

  const handleBannerClick = () => {
    if (!cta || !ctaLink) return;
    gtmClickCta({ name: cta, location: pageType });
    gtmHeroInteraction('click', title + (subtitle ? ` | ${subtitle}` : ''), index || 0);
  };
  const labelId = useId();
  const subtitleId = `${labelId}-subtitle`;
  const titleId = `${labelId}-title`;
  const ctaId = `${labelId}-cta`;

  const renderCopyContent = (): ReactElement => {
    if (!hasCopyContent) {
      return <></>;
    }
    return (
      <div
        className={cn(
          `absolute h-fit text-white max-lg:left-1/2 max-lg:top-1/2 max-lg:m-0 max-lg:w-max max-lg:max-w-[80%] max-lg:-translate-x-1/2 max-lg:-translate-y-1/2 ${classNames(HeroBannerCopyPlacement[copyPlacement ?? 'MIDDLE'])} flex flex-col items-center gap-3 rounded-[6px] bg-black/40 p-[36px] transition-all duration-500`,
          { 'opacity-0': !isOnCarouselView, 'mx-[48px]': copyPlacement !== 'MIDDLE' },
        )}
      >
        <div
          className={classNames('flex gap-3 text-center', {
            'flex-col-reverse': !cta,
            'flex-col': cta !== undefined,
          })}
        >
          {subtitle?.trim() && (
            <p
              id={subtitleId}
              className="block text-center text-sm font-normal leading-[17px] lg:text-lg lg:leading-[21px]"
            >
              {subtitle}
            </p>
          )}
          {title?.trim() && (
            <h2 id={titleId} className="text-[28px] font-semibold leading-[34px]">
              {title}
            </h2>
          )}
        </div>
        {cta?.trim() && ctaLink?.trim() && (
          <div
            id={ctaId}
            className="cursor-pointer rounded-md bg-[rgb(60,60,60)] px-5 py-2 text-center text-xs font-medium leading-[17px] hover:bg-black lg:text-sm"
            aria-hidden="true"
          >
            {cta}
          </div>
        )}
      </div>
    );
  };

  const innerDiv = (
    <div
      className={cn('relative w-full transition-all', {
        'h-full': isFromCarousel,
        'opacity-30': !isOnCarouselView,
      })}
    >
      <GTMWrapper
        className={cn('relative size-full', isFromCarousel ? 'lg:h-[630px]' : 'h-[430px]')}
        gtmEvents={gtmEvents}
      >
        <>
          <picture className="contents">
            <source media="(min-width: 1024px)" srcSet={desktopSrcSet} sizes={`${layout.pageMaxWidth}px`} />
            <img {...mobileImageProps} className="object-cover lg:rounded-md" />
          </picture>

          {renderCopyContent()}
        </>
      </GTMWrapper>
    </div>
  );

  if (ctaLink) {
    return (
      <Link
        href={ctaLink}
        onClick={handleBannerClick}
        className={cn('block w-full cursor-pointer', { 'h-full': isFromCarousel })}
        aria-hidden={!isOnCarouselView}
        aria-labelledby={`${subtitleId} ${titleId} ${ctaId}`}
        style={{ pointerEvents: isOnCarouselView ? 'auto' : 'none' }}
        tabIndex={isOnCarouselView ? undefined : -1}
      >
        {innerDiv}
      </Link>
    );
  }

  return innerDiv;
};

export default AmplienceHeroBanner;
